import React from "react";
import { useTranslation } from "react-i18next";
import SimCardIcon from "./SimCardIcon";
import useIsUSVisitor from "../lib/useIsUSVisitor";
import { openCookiePreferences } from "../lib/cookieConsent";
import PaymentLogos from "./PaymentLogos";

export default function Footer() {
  const { t } = useTranslation();
  const isUS = useIsUSVisitor();

  return (
    <footer className="mt-28" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="bg-white rounded-t-[2.5rem] border border-ink-200 border-b-0 shadow-soft pt-14 pb-10 px-8 lg:px-14">
          {/* Top: 4 clean columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Brand + tagline + corporate details */}
            <div className="col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 bg-cobalt-500 rounded-full flex items-center justify-center shadow-soft">
                  <SimCardIcon size={20} className="text-white" />
                </div>
                <span className="font-display font-bold text-2xl tracking-tight">{t("common.brand")}</span>
              </div>
              <p className="text-ink-700 max-w-sm">{t("footer.tagline")}</p>
              <address
                className="mt-6 not-italic text-xs text-ink-500 leading-relaxed"
                data-testid="footer-company-details"
              >
                <div className="font-semibold text-ink-700">BLUEROAM LTD</div>
                <div>{t("footer.companyForm", "A private company limited by shares incorporated in England and Wales.")}</div>
                <div>{t("footer.companyNumber", "Company number")}: 17215030</div>
                <div>{t("footer.regOffice", "Registered office")}: 128 City Road,<br />London, EC1V 2NX, United Kingdom</div>
                <div className="mt-2">
                  <a
                    href="mailto:hello@blueroam.io"
                    className="hover:text-cobalt-600 font-medium"
                    data-testid="footer-email"
                  >
                    hello@blueroam.io
                  </a>
                </div>
              </address>
            </div>

            {/* Product */}
            <div>
              <div className="overline mb-4">{t("footer.product")}</div>
              <ul className="space-y-2.5 text-sm text-ink-700">
                <li><a href="/plans" className="hover:text-cobalt-600">{t("nav.plans")}</a></li>
                <li><a href="/payfly" className="hover:text-cobalt-600">{t("common.payfly")}</a></li>
                <li><a href="/calculator" className="hover:text-cobalt-600">{t("common.calculator")}</a></li>
                <li><a href="/#faq" className="hover:text-cobalt-600">FAQ</a></li>
                <li>
                  <a
                    href="mailto:hello@blueroam.io"
                    className="hover:text-cobalt-600"
                    data-testid="footer-contact"
                  >
                    {t("footer.contact")}
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal — 5 core policies only */}
            <div>
              <div className="overline mb-4">{t("footer.legal")}</div>
              <ul className="space-y-2.5 text-sm text-ink-700">
                <li><a href="/legal/terms" className="hover:text-cobalt-600" data-testid="footer-terms">{t("footer.terms")}</a></li>
                <li><a href="/legal/privacy" className="hover:text-cobalt-600" data-testid="footer-privacy">{t("footer.privacy")}</a></li>
                <li><a href="/legal/refunds" className="hover:text-cobalt-600" data-testid="footer-refunds">{t("footer.refunds", "Refund policy")}</a></li>
                <li><a href="/legal/cookies" className="hover:text-cobalt-600" data-testid="footer-cookies">{t("footer.cookies")}</a></li>
                <li><a href="/legal/aml-kyc" className="hover:text-cobalt-600" data-testid="footer-amlkyc">{t("footer.amlkyc", "AML / KYC policy")}</a></li>
              </ul>
            </div>
          </div>

          {/* Compliance utility row — discoverable but not noisy.
              Houses secondary / specialist policies and self-serve actions
              that compliance + US-state laws require to be one click away. */}
          <div
            className="mt-10 pt-6 border-t border-ink-100 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-500"
            data-testid="footer-compliance-row"
          >
            <span className="font-semibold text-ink-700 uppercase tracking-widest text-[11px]">
              {t("footer.compliance", "Compliance")}
            </span>
            <a href="/legal/website-terms" className="hover:text-cobalt-600" data-testid="footer-website-terms">
              {t("footer.websiteTerms", "Website terms")}
            </a>
            <a href="/legal/us-privacy" className="hover:text-cobalt-600" data-testid="footer-us-privacy">
              {t("footer.usPrivacy", "US privacy notice")}
            </a>
            <a href="/legal/privacy-request" className="hover:text-cobalt-600" data-testid="footer-privacy-request">
              {t("footer.privacyRequest", "Submit a privacy request")}
            </a>
            <button
              type="button"
              onClick={openCookiePreferences}
              className="hover:text-cobalt-600"
              data-testid="footer-cookie-prefs"
            >
              {t("footer.cookiePrefs", "Cookie preferences")}
            </button>
            {isUS && (
              <>
                <a
                  href="/legal/do-not-sell"
                  className="hover:text-cobalt-600 font-semibold text-cobalt-600"
                  data-testid="footer-do-not-sell"
                >
                  🇺🇸 {t("footer.doNotSell", "Do Not Sell or Share My Personal Information")}
                </a>
                <a
                  href="/legal/limit-sensitive-pi"
                  className="hover:text-cobalt-600 font-semibold text-cobalt-600"
                  data-testid="footer-limit-sensitive"
                >
                  🇺🇸 {t("footer.limitSensitive", "Limit the Use of My Sensitive Personal Information")}
                </a>
              </>
            )}
          </div>

          {/* Copyright */}
          <div className="mt-6 pt-5 border-t border-ink-100 text-xs text-ink-500 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <span>
              © {new Date().getFullYear()} BlueRoam Ltd · {t("footer.copyrightSuffix", "Registered in England & Wales No. 17215030.")}
            </span>
            <div className="flex items-center gap-4">
              <PaymentLogos size={20} testid="footer-payment-logos" />
              <span className="flex items-center gap-1 whitespace-nowrap">🌍 120+ · 🔒</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
